import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  ScanBatch, ScanDocument, SignatureZone, ZoneCheck,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";
import { odmien } from "../lib/polski";

/**
 * Skanowanie zwrotne podpisanych umów (§4.9).
 *
 * **Sprawozdanie z partii jest na górze i jest tu najważniejsze.** „Zeskanowano
 * 28 z 41, brakuje: […]” to jedyna liczba, której ktoś naprawdę użyje: produkcja
 * dowiaduje się wieczorem, kogo dobrać nazajutrz. Lista dokumentów pod spodem
 * odpowiada na pytanie „dlaczego akurat tylu”, ale sama w sobie nie jest
 * odpowiedzią.
 *
 * **Trzy stany, nie dwa.** Detekcja podpisu jest jedynym probabilistycznym
 * elementem systemu, więc „nie wiem” ma własny stan i zawsze trafia do
 * człowieka. Fałszywy pozytyw przepuszcza niepodpisaną umowę do kadr — szkoda
 * nieodwracalna; fałszywy negatyw kosztuje minutę czyjejś pracy.
 */

const STAN: Record<string, "success" | "warning" | "error" | "default"> = {
  podpisana: "success",
  do_przejrzenia: "warning",
  kwarantanna: "error",
  powtorka: "default",
};

export function ScansPage() {
  const { code = "" } = useParams();
  const { can } = useAuth();
  const kolejka = useQueryClient();
  const [blad, setBlad] = useState<ApiError | null>(null);
  const [otwarta, setOtwarta] = useState<string | null>(null);
  const [potwierdzany, setPotwierdzany] = useState<ScanDocument | null>(null);

  const { data: partie } = useQuery({
    queryKey: ["scans", code],
    queryFn: () => api.get<{ results: ScanBatch[] }>(`/projects/${code}/scans`),
  });

  const { data: pola } = useQuery({
    queryKey: ["signature-zones"],
    queryFn: () => api.get<{ results: SignatureZone[]; can_manage: boolean }>(
      "/signature-zones",
    ),
  });

  const { data: szczegoly } = useQuery({
    queryKey: ["scan", otwarta],
    queryFn: () => api.get<ScanBatch>(`/scans/${otwarta}`),
    enabled: Boolean(otwarta),
  });

  const wgraj = useMutation({
    mutationFn: (plik: File) => {
      const dane = new FormData();
      dane.append("file", plik);
      return api.postForm<ScanBatch>(`/projects/${code}/scans`, dane);
    },
    onSuccess: (partia) => {
      setBlad(null);
      setOtwarta(partia.id);
      kolejka.invalidateQueries({ queryKey: ["scans", code] });
      kolejka.invalidateQueries({ queryKey: ["contracts", code] });
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const potwierdz = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post<ScanDocument>(`/scan-documents/${id}/confirm`, { note }),
    onSuccess: () => {
      setPotwierdzany(null);
      setBlad(null);
      kolejka.invalidateQueries({ queryKey: ["scan", otwarta] });
      kolejka.invalidateQueries({ queryKey: ["scans", code] });
      kolejka.invalidateQueries({ queryKey: ["contracts", code] });
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const sprawdzPola = useMutation({
    mutationFn: () => api.post<{ results: ZoneCheck[] }>("/signature-zones/check", {}),
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const brakPol = pola && pola.results.length === 0;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <BackButton fallback={`/projekty/${code}`} />
        <Typography variant="h6">{code} · skany umów</Typography>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => sprawdzPola.mutate()} disabled={sprawdzPola.isPending}>
          Sprawdź pola podpisu
        </Button>
        {can("contract.generate") && (
          <Button component="label" variant="contained"
                  disabled={wgraj.isPending}
                  startIcon={wgraj.isPending
                    ? <CircularProgress size={16} /> : <UploadFileIcon />}>
            {wgraj.isPending ? "Przetwarzam partię…" : "Wgraj partię ze skanera"}
            <input hidden type="file" accept="application/pdf"
                   onChange={(e) => {
                     const plik = e.target.files?.[0];
                     e.target.value = "";
                     if (plik) wgraj.mutate(plik);
                   }} />
          </Button>
        )}
      </Stack>

      {blad && (
        <Alert severity="error" onClose={() => setBlad(null)}>
          {blad.message}
          {blad.body.problems.length > 0 && (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {blad.body.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
        </Alert>
      )}

      {brakPol && (
        <Alert severity="warning">
          Nie skonfigurowano pól podpisu, więc <strong>każdy</strong> skan trafi
          do ręcznego przejrzenia. Przetwarzanie zadziała, ale nie oszczędzi
          pracy — pola ustawia administrator w panelu administracyjnym.
        </Alert>
      )}

      {sprawdzPola.data && <WynikSprawdzenia wyniki={sprawdzPola.data.results} />}

      <Alert severity="info" variant="outlined">
        Skanuj w 300 dpi, w skali szarości, z prostowaniem obrazu i{" "}
        <strong>wyłączonym</strong> usuwaniem pustych stron. Przekładki z teczki
        dnia 1 wyrzuć — nie mają kodu i trafią do kwarantanny. Dwa egzemplarze
        tej samej umowy skanuj osobno: mają ten sam kod, więc razem wyglądają
        jak jeden dokument o podwójnej liczbie stron.
      </Alert>

      <Paper variant="outlined">
        <Stack direction="row" alignItems="center" spacing={1}
               sx={{ px: 2, py: 1.25, bgcolor: "action.hover" }}>
          <Typography variant="subtitle2">Partie</Typography>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plik</TableCell>
              <TableCell align="right">Stron</TableCell>
              <TableCell>Zebrano</TableCell>
              <TableCell>Wynik</TableCell>
              <TableCell>Wgrał</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(partie?.results ?? []).map((p) => (
              <TableRow key={p.id} hover selected={p.id === otwarta}
                        sx={{ cursor: "pointer" }}
                        onClick={() => setOtwarta(p.id === otwarta ? null : p.id)}>
                <TableCell>
                  {p.filename}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {p.processed_at?.slice(0, 16).replace("T", " ") ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">{p.page_count}</TableCell>
                <TableCell>
                  {p.summary.expected !== undefined && (
                    <strong>{p.summary.collected} z {p.summary.expected}</strong>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {(p.summary.signed ?? 0) > 0 && (
                      <Chip size="small" color="success" variant="outlined"
                            label={`podpisane ${p.summary.signed}`} />
                    )}
                    {(p.summary.review ?? 0) > 0 && (
                      <Chip size="small" color="warning" variant="outlined"
                            label={`do przejrzenia ${p.summary.review}`} />
                    )}
                    {(p.summary.quarantine ?? 0) > 0 && (
                      <Chip size="small" color="error" variant="outlined"
                            label={`kwarantanna ${p.summary.quarantine}`} />
                    )}
                    {(p.summary.duplicates ?? 0) > 0 && (
                      <Chip size="small" variant="outlined"
                            label={`powtórki ${p.summary.duplicates}`} />
                    )}
                    {p.state === "bledna" && (
                      <Chip size="small" color="error" label="nieprzetworzona" />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{p.uploaded_by_label}</TableCell>
                <TableCell align="right">
                  <Button size="small">
                    {p.id === otwarta ? "Zwiń" : "Sprawozdanie"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(partie?.results ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Nie wgrano jeszcze żadnej partii.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {szczegoly && <Sprawozdanie partia={szczegoly}
                                  onPotwierdz={setPotwierdzany} />}

      <PotwierdzDialog
        dokument={potwierdzany}
        pracuje={potwierdz.isPending}
        onClose={() => setPotwierdzany(null)}
        onConfirm={(note) =>
          potwierdzany && potwierdz.mutate({ id: potwierdzany.id, note })}
      />
    </Stack>
  );
}

function Sprawozdanie({ partia, onPotwierdz }: {
  partia: ScanBatch;
  onPotwierdz: (d: ScanDocument) => void;
}) {
  const s = partia.summary;
  const brakuje = s.missing ?? [];

  return (
    <Stack spacing={2}>
      {/* To jest odpowiedź, po którą ktoś tu przychodzi: kogo brakuje. */}
      <Alert severity={brakuje.length === 0 ? "success" : "warning"}>
        <Typography variant="body2" fontWeight={600}>
          Zebrano {s.collected} z{" "}
          {s.expected} {odmien(s.expected ?? 0, "umowy", "umów", "umów")}
          {" · "}{partia.page_count} str. w partii
        </Typography>
        {brakuje.length > 0 && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Brakuje podpisu: {brakuje.map((m) => m.person).join(", ")}.
          </Typography>
        )}
      </Alert>

      {partia.error && <Alert severity="error">{partia.error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Strony</TableCell>
              <TableCell>Umowa</TableCell>
              <TableCell>Osoba</TableCell>
              <TableCell>Stan</TableCell>
              <TableCell>Podpisy</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(partia.documents ?? []).map((d) => (
              <TableRow key={d.id} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {d.first_page === d.last_page
                    ? d.first_page : `${d.first_page}–${d.last_page}`}
                </TableCell>
                <TableCell>{d.contract_number || "—"}</TableCell>
                <TableCell>{d.person_label || "—"}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={d.state_label}
                        color={STAN[d.state] ?? "default"} />
                  {d.problem_detail && (
                    <Typography variant="caption" color="text.secondary"
                                display="block" sx={{ maxWidth: 420 }}>
                      {d.problem_detail}
                    </Typography>
                  )}
                  {d.confirmed_by_label && (
                    <Typography variant="caption" color="text.secondary"
                                display="block">
                      potwierdził: {d.confirmed_by_label}
                      {d.confirm_note && ` — ${d.confirm_note}`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {Object.entries(d.signatures ?? {}).map(([strona, w]) => (
                      <Tooltip key={strona}
                               title={`pokrycie ${w.coverage}${
                                 w.note ? ` · ${w.note}` : ""}`}>
                        <Chip size="small" variant="outlined"
                              label={`${strona}: ${w.verdict}`}
                              color={w.verdict === "wykryto" ? "success"
                                : w.verdict === "brak" ? "error" : "warning"} />
                      </Tooltip>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Button size="small" startIcon={<PictureAsPdfIcon />}
                            href={`/api/scan-documents/${d.id}/file`}
                            target="_blank" rel="noopener">
                      Skan
                    </Button>
                    {d.state === "do_przejrzenia" && (
                      <Button size="small" color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => onPotwierdz(d)}>
                        Potwierdź podpis
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

function WynikSprawdzenia({ wyniki }: { wyniki: ZoneCheck[] }) {
  const zle = wyniki.filter((w) => w.ok === false);
  const nieznane = wyniki.filter((w) => w.ok === null);

  return (
    <Alert severity={zle.length > 0 ? "error" : nieznane.length > 0 ? "info" : "success"}>
      <Typography variant="body2" fontWeight={600}>
        Pola podpisu zmierzone na niepodpisanym wydruku
      </Typography>
      {zle.length === 0 && nieznane.length === 0 && (
        <Typography variant="body2">
          Wszystkie {wyniki.length} pól czyste — pusty formularz nie przejdzie
          jako podpisany.
        </Typography>
      )}
      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
        {[...zle, ...nieznane].map((w) => (
          <li key={w.id}>
            <Typography variant="body2">
              {w.form_label} · {w.party_label}: {w.note}
            </Typography>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function PotwierdzDialog({ dokument, pracuje, onClose, onConfirm }: {
  dokument: ScanDocument | null;
  pracuje: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const [notatka, setNotatka] = useState("");

  return (
    <Dialog open={dokument !== null} onClose={onClose} fullWidth maxWidth="sm"
            TransitionProps={{ onEnter: () => setNotatka("") }}>
      <DialogTitle>Potwierdź podpis · {dokument?.contract_number}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Od tej chwili umowa jest podpisana na podstawie <strong>Twojej
          decyzji</strong>, a nie pomiaru — dlatego zostaje ślad, kto ją podjął.
          Otwórz skan i sprawdź pole podpisu, zanim potwierdzisz.
        </DialogContentText>
        <TextField
          autoFocus fullWidth multiline minRows={2} value={notatka}
          label="Notatka (opcjonalna)"
          helperText="Powtarzający się rozjazd między detekcją a potwierdzeniem
                      znaczy, że pole podpisu stoi w złym miejscu."
          onChange={(e) => setNotatka(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" color="success" disabled={pracuje}
                onClick={() => onConfirm(notatka.trim())}>
          Potwierdź podpis
        </Button>
      </DialogActions>
    </Dialog>
  );
}
